// forntend(root)/src/hooks/useActivityFeed.js
import { useState, useEffect, useCallback } from 'react';
import { activityApi } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const useActivityFeed = (initialFilter = 'all') => {
  const { isLoggedIn } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalActivities, setTotalActivities] = useState(0);

  // Fetch activities based on current filter and page
  const fetchActivities = useCallback(async (reset = false) => {
    if (!isLoggedIn) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const currentPage = reset ? 1 : page;
      const response = await activityApi.getActivityFeed(filter, currentPage);
      
      if (response.success) {
        const newActivities = response.data;
        setActivities(prev => reset ? newActivities : [...prev, ...newActivities]);
        setTotalActivities(response.total);
        setHasMore(currentPage < response.totalPages);
        
        if (reset) {
          setPage(1);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load activities');
      console.error('Error fetching activity feed:', err);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, filter, page]);

  // Load initial data
  useEffect(() => {
    fetchActivities(true);
  }, [filter, isLoggedIn]);

  // Function to load more activities
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      setPage(prevPage => prevPage + 1);
    }
  }, [loading, hasMore]);

  // When page changes (and it's not the first page), fetch more
  useEffect(() => {
    if (page > 1) {
      fetchActivities();
    }
  }, [page, fetchActivities]);

  // Change filter and reset state
  const changeFilter = useCallback((newFilter) => {
    if (newFilter !== filter) {
      setFilter(newFilter);
      // The first useEffect will trigger with the new filter and reset flag
    }
  }, [filter]);

  // Refresh activities
  const refresh = useCallback(() => {
    fetchActivities(true);
  }, [fetchActivities]);

  return {
    activities,
    loading,
    error,
    filter,
    hasMore,
    totalActivities,
    loadMore,
    changeFilter,
    refresh
  };
};

export default useActivityFeed;